/**
 * List of colors used by our color palette.
 *
 * These values can be used in a CSS file as `var(--<colorname>)`.
 *
 * @deprecated Reference CSS variables directly (e.g. `var(--kale-700)`) instead
 *   of importing this enum.
 * @see https://corgi-x.tfd.engineering/system/colors
 */
export enum Color {
  Blueberry0 = '#E6ECF5',
  Blueberry1 = '#B7C6E6',
  Blueberry2 = '#385A9D',
  Blueberry3 = '#03184E',
  /** @deprecated Use `Carrot200`. */
  Carrot0 = '#FFF4F1',
  Carrot1 = '#FFD9CC',
  /** @deprecated Use `Carrot500`. */
  Carrot2 = '#F2674B',
  /** @deprecated Use `Carrot600`. */
  Carrot3 = '#D2563D',
  Carrot200 = '#FFF4F1', // eslint-disable-line @typescript-eslint/no-duplicate-enum-values
  Carrot500 = '#F2674B', // eslint-disable-line @typescript-eslint/no-duplicate-enum-values
  Carrot600 = '#D2563D', // eslint-disable-line @typescript-eslint/no-duplicate-enum-values
  Charcoal0 = '#C2C3C5',
  Charcoal1 = '#969799',
  Charcoal2 = '#717377',
  Charcoal3 = '#39383C',
  Chickpea0 = '#FFFAF3',
  Chickpea1 = '#FFF3E1',
  Chickpea2 = '#F2E1C8',
  Chickpea3 = '#D3BFA2',
  ChickpeaExperimental = '#FFFCF8',
  /* TODO: change this variable name */
  /* https://app.shortcut.com/farmersdog/story/134455/revise-new-chickpea-name */
  DarkChickpeaExperimental = '#FBF7EF',
  Cranberry0 = '#FEF1F3',
  Cranberry1 = '#F7C1CA',
  Cranberry2 = '#D24A61',
  Cranberry3 = '#982235',
  Error800 = '#B80000',
  /** @deprecated Use `Kale300`. */
  Kale0 = '#E5EFE7',
  Kale1 = '#B5D3BA',
  Kale2 = '#417C67',
  /** @deprecated Use `Kale900`. */
  Kale3 = '#173B33',
  /** @deprecated Use `Kale700`. */
  Kale4 = '#3C725E',
  Kale300 = '#E5EFE7', // eslint-disable-line @typescript-eslint/no-duplicate-enum-values
  Kale700 = '#3C725E', // eslint-disable-line @typescript-eslint/no-duplicate-enum-values
  Kale900 = '#173B33', // eslint-disable-line @typescript-eslint/no-duplicate-enum-values
  Oat200 = '#F7F5F0',
  Oat300 = '#EDE8D9',
  Oyster0 = '#F6F5F3',
  Oyster1 = '#EFEDEA',
  Oyster2 = '#DBD6D1',
  Oyster3 = '#C6BFB7',
  Parsnip300 = '#E7E8E7',
  Sage400 = '#CCD4CE',
  Sage700 = '#5A6C61',
  White = '#FFFFFF',
  /** @deprecated Use `Cranberry3`. */
  ErrorRed = '#982235', // eslint-disable-line @typescript-eslint/no-duplicate-enum-values
  /** @deprecated Use `Cranberry3`. */
  ErrorRedDark = '#982235', // eslint-disable-line @typescript-eslint/no-duplicate-enum-values
  /** @deprecated Use `Cranberry0`. */
  ErrorRedBg = '#FEF1F3', // eslint-disable-line @typescript-eslint/no-duplicate-enum-values
  TfdLogo = '#F5694D',
  Twitter = '#1DA1F2',
  Facebook = '#3B5998',
  Instagram = '#6BA5D5',
}

/** The name of a color in our color palette. */
export type ColorName = keyof typeof Color;

/** Return whether `value` is a name of one of the palette {@link Color}. */
export function isColorName(value: string): value is ColorName {
  return value in Color;
}

/**
 * Given a color string returns the {@link Color} representing it, or - if not
 * available - the given color.
 *
 * This function is useful to pass color props either from our palette's color
 * name, a {@link Color} or any CSS color.
 */
export function getColor(colorOrName: string): Color | string {
  if (isColorName(colorOrName)) {
    return Color[colorOrName];
  }
  return colorOrName;
}
